/**
 * Shared FIT/FILL transform for Resize prep preview and CanvasEditor.
 * FIT = contain (full image, empty space). FILL = cover (fills canvas, center crop).
 */
export function getTransform(iw, ih, cw, ch, mode = "fit") {
  const imageW = Number(iw);
  const imageH = Number(ih);
  const canvasW = Number(cw);
  const canvasH = Number(ch);

  if (
    !imageW ||
    !imageH ||
    !canvasW ||
    !canvasH ||
    imageW <= 0 ||
    imageH <= 0 ||
    canvasW <= 0 ||
    canvasH <= 0
  ) {
    return { scale: 1, width: 0, height: 0, x: 0, y: 0 };
  }

  const scale =
    mode === "fill"
      ? Math.max(canvasW / imageW, canvasH / imageH)
      : Math.min(canvasW / imageW, canvasH / imageH);

  const width = imageW * scale;
  const height = imageH * scale;
  const x = (canvasW - width) / 2;
  const y = (canvasH - height) / 2;

  return { scale, width, height, x, y };
}

function gcdInt(a, b) {
  let x = Math.round(Math.abs(a));
  let y = Math.round(Math.abs(b));
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

/** e.g. "1000 × 1080 px" */
export function formatPixelDimensions(width, height) {
  const w = Math.round(Number(width));
  const h = Math.round(Number(height));
  if (!(w > 0 && h > 0)) return "";
  return `${w} × ${h} px`;
}

/** Lowest-terms aspect, e.g. "16:9". */
export function formatAspectRatioLabel(width, height) {
  const w = Math.round(Number(width));
  const h = Math.round(Number(height));
  if (!(w > 0 && h > 0)) return "";
  const g = gcdInt(w, h);
  return `${Math.round(w / g)}:${Math.round(h / g)}`;
}

/** Ensure a size label ends with "px". */
export function ensurePixelUnitLabel(label) {
  const s = String(label || "").trim();
  if (!s || s === "--") return s;
  if (/\bpx\b/i.test(s)) return s;
  if (/\d/.test(s)) return `${s} px`;
  return s;
}

/**
 * Parse preset pixel size from width/height fields or dims strings
 * (e.g. "1080 × 1920 (9:16)" / legacy "1080 × 1920, 9:16").
 * Prefer numeric API fields — `label` is now the shape name (Square), not dims.
 * @returns {{ width: number, height: number, aspectRatio: number, resolutionLabel: string, aspectRatioLabel: string } | null}
 */
export function parsePresetPixelSize(preset) {
  if (!preset) return null;

  const numericW = Number(preset.width);
  const numericH = Number(preset.height);
  if (
    Number.isFinite(numericW) &&
    Number.isFinite(numericH) &&
    numericW > 0 &&
    numericH > 0
  ) {
    const aspectRatioLabel =
      String(preset.aspect_ratio || "").trim() ||
      formatAspectRatioLabel(numericW, numericH);
    return {
      width: numericW,
      height: numericH,
      aspectRatio: numericW / numericH,
      resolutionLabel: formatPixelDimensions(numericW, numericH),
      aspectRatioLabel,
    };
  }

  const dimensionsStr = String(
    preset.dimensions ?? preset.dims_label ?? ""
  ).trim();
  if (!dimensionsStr || dimensionsStr.toLowerCase().includes("make your size")) {
    return null;
  }

  const parts = dimensionsStr.split(",");
  let resolutionLabel = parts[0] ? parts[0].trim() : "";
  let aspectRatioLabel = parts[1] ? parts[1].trim() : "";
  if (!aspectRatioLabel) {
    const parenAr = dimensionsStr.match(/\(([^)]+)\)/);
    if (parenAr) aspectRatioLabel = parenAr[1].trim();
  }

  let pixelMatch = dimensionsStr.match(/(\d+)\s*×\s*(\d+)/);
  if (!pixelMatch) pixelMatch = dimensionsStr.match(/(\d+)\s*x\s*(\d+)/i);
  if (!pixelMatch) pixelMatch = dimensionsStr.match(/(\d+)\s*[*×xX]\s*(\d+)/);

  if (pixelMatch) {
    const width = parseFloat(pixelMatch[1]);
    const height = parseFloat(pixelMatch[2]);
    if (width > 0 && height > 0) {
      const cleaned =
        resolutionLabel.replace(/\s*\([^)]*\)\s*$/, "").trim() ||
        `${width} × ${height}`;
      return {
        width,
        height,
        aspectRatio: width / height,
        resolutionLabel: ensurePixelUnitLabel(cleaned),
        aspectRatioLabel:
          aspectRatioLabel ||
          String(preset.aspect_ratio || "").trim() ||
          formatAspectRatioLabel(width, height),
      };
    }
  }

  const ratioMatch = dimensionsStr.match(/(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)/);
  if (ratioMatch) {
    const rw = parseFloat(ratioMatch[1]);
    const rh = parseFloat(ratioMatch[2]);
    if (rw > 0 && rh > 0) {
      return {
        width: rw,
        height: rh,
        aspectRatio: rw / rh,
        resolutionLabel: ensurePixelUnitLabel(resolutionLabel),
        aspectRatioLabel: aspectRatioLabel || `${rw}:${rh}`,
      };
    }
  }

  return null;
}
